
import * as THREE from "../../libs/three.js/build/three.module.js";
import {
	findNearbyEndpointGaps,
	findCoincidentEndpointGroups,
	findZeroLengthSegments,
	findSelfIntersections,
	findInvalidPolygons,
} from "./geometry/topologyQa.js";
import {connectEndpoints} from "./geometry/lineSplit.js";
import {settings} from "./settings/DigitizationSettings.js";
import {markDigitizationUI} from "./DialogHelpers.js";

import {t} from "./i18n/index.js";
// User-triggered Topologi-tjek. Runs every read-only scan in geometry/topologyQa.js against the project's CURRENT
// feature list; nothing here mutates any feature except the one explicit "Luk gap" action per gap finding, which calls the
// exact same geometry/lineSplit.js's connectEndpoints() the vertex-menu's "Forbind til andet endpoint…" uses (task:
// "identificere og præsentere problemer frem for automatisk at ændre brugerens data" - only an explicit, per-finding user
// click ever changes anything).
//
// Ejerskab (trin 2E2): Potree ejer scanningen, geometrien, "Luk gap" og kamera-/zoomhandlingerne (scanTopology(),
// zoomToTopologyFinding(), closeTopologyGap() nedenfor - også tilgængelige som dm.scanTopology() osv.). I PointView er
// RESULTATLISTEN et ikke-modalt Vue-Panel (frontendens topology-panel i en float-panel), som kalder disse funktioner; der er
// ingen parallel state i Potree. Uden vært (standalone Potree) bruger runTopologyQaScan() den oprindelige DOM-dialog nedenfor.
//
// Deliberately user-triggered, not an automatic background scan (task: "kontrolleret brugeroperation"; also avoids a
// performance surprise re-scanning on every edit of a large project).

function formatLength(value){
	return `${value.toFixed(3)} m`;
}

function zoomToPosition(dm, position, radius){
	const r = radius || 2;
	const box = new THREE.Box3(
		new THREE.Vector3(position.x - r, position.y - r, position.z - r),
		new THREE.Vector3(position.x + r, position.y + r, position.z + r),
	);
	const node = new THREE.Object3D();
	node.boundingBox = box;
	dm.viewer.zoomTo(node, 1, 500);
}

// Ren geometrisk bounding box ud fra featurens egne, GeoJSON-formede
// koordinater (Feature.js) - IKKE fra dens Visual. Root cause (fejlopfølgning,
// 2026-10): zoomToFeature() brugte før new THREE.Box3().setFromObject(visual),
// som også medregner Visualens håndtag/kugler - de holdes BEVIDST i konstant
// SKÆRMSTØRRELSE uanset kameraafstand (FeatureVisual.js's updateSphereScale()/
// computeScreenSpaceScale()), så deres VERDENS-størrelse er proportional med
// hvor langt kameraet allerede var væk. Jo længere væk kameraet var, jo større
// blev den udledte boks - og jo længere væk flyttede zoomberegningen kameraet
// næste gang. Samme minimumsradius (2 m) som zoomToPosition() ovenfor giver en
// fornuftig visning af et enkelt punkt, som ellers ville give en nulstor boks.
function boundingBoxFromGeometry(geometry){
	const box = new THREE.Box3();
	const add = (p) => box.expandByPoint(new THREE.Vector3(p[0], p[1], p[2] || 0));
	const type = geometry && geometry.type;
	const coords = geometry && geometry.coordinates;
	if(type === "Point") add(coords);
	else if(type === "LineString") coords.forEach(add);
	else if(type === "Polygon") coords.forEach(ring => ring.forEach(add));
	else return null;
	if(box.isEmpty()) return null;
	box.expandByScalar(2);
	return box;
}

// Whole-feature "Zoom til" (kontekstmenuen i Registreringer-træet, og de
// topologi-fund nedenfor der zoomer til en HEL feature frem for ét punkt).
export function zoomToFeature(dm, featureId){
	const feature = dm.featureManager ? dm.featureManager.get(featureId) : null;
	if(!feature) return;
	const box = boundingBoxFromGeometry(feature.geometry);
	if(!box) return;
	const node = new THREE.Object3D();
	// Tættere framing (brugerønske) - inspireret af "T"-genvejens egen
	// begrundelse for IKKE at bruge den almindelige bounding-SFÆRE-tilgang
	// (se PointViewTopFraming.js/topFramingMath.js's "Deliberately NOT the
	// existing 3D bounding-sphere fit"): node.boundingBox.getBoundingSphere()
	// (som camera.zoomTo() ellers selv ville falde tilbage til) bruger
	// boksens DIAGONAL som radius - for en terningformet/flad boks (fx et
	// enkelt, paddet punkt) er diagonalen markant større end boksens
	// faktiske udstrækning, hvilket giver en løsere (fjernere) zoom end
	// nødvendigt. camera.zoomTo()/viewer.zoomTo() er fortsat helt urørte -
	// de foretrækker allerede node.boundingSphere frem for at udlede den
	// fra node.boundingBox, hvis den er sat eksplicit (se deres egen kode);
	// her gives i stedet en sfære med radius = halvdelen af boksens STØRSTE
	// enkeltdimension (footprint-baseret, ikke diagonal-baseret - samme
	// grundprincip som "T", uden at genbruge dens topdown-specifikke
	// kamera-retning, som "Zoom til" bevidst IKKE skal ændre).
	const size = box.getSize(new THREE.Vector3());
	const tightRadius = Math.max(size.x, size.y, size.z) / 2;
	node.boundingSphere = new THREE.Sphere(box.getCenter(new THREE.Vector3()), tightRadius);
	// Endnu tættere framing (brugerønske, 2026-10-01): halveret afstand ift.
	// ovenstående sfære - camera.zoomTo()/viewer.zoomTo()'s egen formel
	// (distanceFactor = radius/sin(fovr/2) * factor) skalerer afstanden
	// lineært med factor, så 0.5 halverer den uden at ændre selve
	// zoomalgoritmen.
	dm.viewer.zoomTo(node, 0.5, 500);
}

function row(label, actionsHtml){
	return `<div style="display:flex; justify-content:space-between; align-items:center; gap:8px; padding:4px 0; border-bottom:1px solid #ffffff14;">` +
		`<span style="font-size:12px; line-height:1.4;">${label}</span>` +
		`<span style="display:flex; gap:4px; flex-shrink:0;">${actionsHtml || ""}</span>` +
		`</div>`;
}

function zoomButtonHtml(key){
	return `<button data-zoom="${key}" style="padding:2px 8px; border-radius:3px; border:1px solid #666; background:#3a3a3a; color:#fff; cursor:pointer; font-size:11px;">${t("Zoom til")}</button>`;
}

// Gap-handling audit finding (task: "Undersøg om dette giver korrekt og
// forudsigelig adfærd") - a single unlabelled "Luk gap" button always moved
// whichever feature happened to be `gap.a` (an accident of scan/discovery
// order, not a deliberate choice) to meet `gap.b`, with no way for the user
// to pick the other direction if that was the wrong side to move - e.g.
// nudging a precisely-surveyed line to meet a rougher sketch, silently,
// because it happened to sort first. connectEndpoints(pointsA, indexA,
// pointsB, indexB) already always moves ITS OWN first argument to the
// second's position (see lineSplit.js) - swapping which feature is passed
// first is the entire fix, so two explicit, named buttons (not a single
// ambiguous one, and not a full automatic-repair menu of every
// theoretically-possible geometric solution - task: "implementér ikke
// automatisk alle muligheder") let the user pick which side actually moves.
function closeGapButtonHtml(index, label, direction){
	return `<button data-closegap="${index}" data-direction="${direction}"` +
		` style="padding:2px 8px; border-radius:3px; border:none; background:#2f8fd6; color:#fff; cursor:pointer; font-size:11px;">${t("Flyt")} ${label}</button>`;
}

function sectionHtml(title, count){
	return `<div style="font-weight:bold; font-size:12px; margin-top:12px; margin-bottom:4px; opacity:0.85;">${title} (${count})</div>`;
}

// Ren data: de fem read-only scans mod den AKTUELLE feature-liste (altid genlæst, aldrig cachet). Bruges af både Vue-panelet
// og standalone-dialogen; resultatet er almindelige objekter (featureId/featureName/point/...), ingen Potree-objekter.
export function scanTopology(dm){
	const features = dm.featureManager.features;
	const planarTolerance = settings.get("topology.planarTolerance");
	return {
		gaps: findNearbyEndpointGaps(features, {minGap: planarTolerance}),
		groups: findCoincidentEndpointGroups(features, {tolerance: planarTolerance}),
		zeroLength: findZeroLengthSegments(features),
		selfIntersections: findSelfIntersections(features),
		invalidPolygons: findInvalidPolygons(features),
	};
}

// "Zoom til" for ét fund. kind: "gap" | "node" (zoom til punktet) eller "zero" | "self" | "poly" (zoom til hele featuren).
// Returnerer false, hvis featuren ikke længere findes (et ikke-modalt panel kan være forældet).
export function zoomToTopologyFinding(dm, kind, finding){
	if(kind === "gap"){ zoomToPosition(dm, finding.a.point); return true; }
	if(kind === "node"){ zoomToPosition(dm, finding.position); return true; }
	if(!dm.featureManager.get(finding.featureId)) return false;
	zoomToFeature(dm, finding.featureId);
	return true;
}

// "Luk gap" (se closeGapButtonHtml() for hvorfor der er to retninger): flytter præcis ÉN feature til at møde den anden via
// connectEndpoints(). Fordi panelet nu er ikke-modalt, kan brugeren have ændret geometri siden scanningen - gappet
// re-valideres derfor mod en frisk scan (samme features + endepunkter + stadig et gap), og ellers gøres intet (returnerer
// false, så kalderen kan opdatere listen). Efterfølgende re-scan er kalderens ansvar (som før: "recompute, never cache").
export function closeTopologyGap(dm, gap, direction){
	const fresh = scanTopology(dm).gaps.find((g) =>
		g.a.featureId === gap.a.featureId && g.a.endpointIndex === gap.a.endpointIndex &&
		g.b.featureId === gap.b.featureId && g.b.endpointIndex === gap.b.endpointIndex);
	if(!fresh) return false;
	const visualA = dm.getVisual(fresh.a.featureId);
	const visualB = dm.getVisual(fresh.b.featureId);
	if(!visualA || !visualB) return false;

	const pointsA = visualA.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));
	const pointsB = visualB.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));

	// "a" moves a to meet b; "b" swaps the connectEndpoints() arguments so b moves to meet a instead; the geometry
	// operation itself is identical either way.
	const [movingVisual, result] = direction === "b"
		? [visualB, connectEndpoints(pointsB, fresh.b.endpointIndex, pointsA, fresh.a.endpointIndex)]
		: [visualA, connectEndpoints(pointsA, fresh.a.endpointIndex, pointsB, fresh.b.endpointIndex)];
	return dm.applyFeatureGeometry(movingVisual, result.points.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
}

// Runs every scan against dm.featureManager.features (the live, current
// feature list - re-read fresh each time this is called, never cached) and
// shows the panel. Exported so sidebar.js's button and this panel's own
// "Luk gap" (which re-scans afterwards, so the panel reflects the just-
// closed gap) both call the exact same entry point.
export function runTopologyQaScan(dm){
	showTopologyQaPanel(dm, scanTopology(dm));
}

function showTopologyQaPanel(dm, results){
	const {gaps, groups, zeroLength, selfIntersections, invalidPolygons} = results;
	const problemCount = gaps.length + zeroLength.length + selfIntersections.length + invalidPolygons.length;

	const overlay = document.createElement("div");
	overlay.style.cssText = [
		"position: fixed", "inset: 0", "background: rgba(0,0,0,0.45)", "z-index: 10002",
		"display: flex", "align-items: center", "justify-content: center", "font-family: Arial, sans-serif",
	].join(";");

	const box = document.createElement("div");
	box.style.cssText = [
		"background: #2b2b2b", "color: #fff", "border-radius: 6px", "padding: 16px 18px",
		"width: 440px", "max-width: calc(100vw - 40px)", "max-height: calc(100vh - 80px)", "overflow-y: auto",
		"box-shadow: 0 4px 18px rgba(0,0,0,0.5)",
	].join(";");

	let html = `<div style="font-weight:bold; font-size:14px; margin-bottom:4px;">${t("Topologi-tjek")}</div>`;

	if(problemCount === 0){
		html += `<div style="font-size:12px; opacity:0.8; margin-top:8px;">${t("Ingen problemer fundet.")}</div>`;
	}

	if(gaps.length > 0){
		html += sectionHtml(t("Mindre gaps mellem endpoints"), gaps.length);
		html += gaps.map((g, i) => row(
			`${g.a.featureName} ↔ ${g.b.featureName}: ${formatLength(g.gap)}`,
			zoomButtonHtml(`gap${i}`) + closeGapButtonHtml(i, g.a.featureName, "a") + closeGapButtonHtml(i, g.b.featureName, "b"),
		)).join("");
	}

	if(zeroLength.length > 0){
		html += sectionHtml(t("Nul-længde segmenter / dublerede vertices"), zeroLength.length);
		html += zeroLength.map((z, i) => row(`${z.featureName} (vertex ${z.vertexIndex})`, zoomButtonHtml(`zero${i}`))).join("");
	}

	if(selfIntersections.length > 0){
		html += sectionHtml(t("Utilsigtede selvskæringer"), selfIntersections.length);
		html += selfIntersections.map((s, i) => row(`${s.featureName} (segment ${s.segIndexA} × ${s.segIndexB})`, zoomButtonHtml(`self${i}`))).join("");
	}

	if(invalidPolygons.length > 0){
		html += sectionHtml(t("Ugyldige polygoner"), invalidPolygons.length);
		html += invalidPolygons.map((p, i) => row(p.featureName, zoomButtonHtml(`poly${i}`))).join("");
	}

	if(groups.length > 0){
		html += sectionHtml(t("Delte forbindelsespunkter (informativ - ikke et problem)"), groups.length);
		html += groups.map((g, i) => row(g.members.map((m) => m.featureName).join(", "), zoomButtonHtml(`node${i}`))).join("");
	}

	html += `<div style="display:flex; justify-content:flex-end; margin-top:14px;">` +
		`<button data-action="close" style="padding:5px 14px; border-radius:3px; border:none; background:#3a3a3a; color:#fff; cursor:pointer;">${t("Luk")}</button>` +
		`</div>`;

	box.innerHTML = html;
	overlay.appendChild(box);
	markDigitizationUI(overlay);
	document.body.appendChild(overlay);

	const close = () => document.body.contains(overlay) && document.body.removeChild(overlay);
	box.querySelector('[data-action="close"]').addEventListener("click", close);
	overlay.addEventListener("mousedown", (e) => { if(e.target === overlay) close(); });
	box.setAttribute("tabindex", "-1");
	box.addEventListener("keydown", (e) => { if(e.key === "Escape"){ e.stopPropagation(); close(); } });
	box.focus();

	box.querySelectorAll('[data-zoom]').forEach((btn) => {
		btn.addEventListener("click", () => {
			const key = btn.getAttribute("data-zoom");
			if(key.indexOf("gap") === 0) zoomToTopologyFinding(dm, "gap", gaps[parseInt(key.slice(3), 10)]);
			else if(key.indexOf("zero") === 0) zoomToTopologyFinding(dm, "zero", zeroLength[parseInt(key.slice(4), 10)]);
			else if(key.indexOf("self") === 0) zoomToTopologyFinding(dm, "self", selfIntersections[parseInt(key.slice(4), 10)]);
			else if(key.indexOf("poly") === 0) zoomToTopologyFinding(dm, "poly", invalidPolygons[parseInt(key.slice(4), 10)]);
			else if(key.indexOf("node") === 0) zoomToTopologyFinding(dm, "node", groups[parseInt(key.slice(4), 10)]);
		});
	});

	// The actionable buttons in this panel - closes exactly the ONE gap the user clicked (closeTopologyGap()), then re-runs
	// the whole scan so the panel (re-opened) reflects the new state, same "recompute, never cache" QA doctrine as every
	// other finding here.
	box.querySelectorAll('[data-closegap]').forEach((btn) => {
		btn.addEventListener("click", () => {
			closeTopologyGap(dm, gaps[parseInt(btn.getAttribute("data-closegap"), 10)], btn.getAttribute("data-direction"));
			close();
			runTopologyQaScan(dm);
		});
	});
}
